import React, { useId, useState } from 'react';
import styled, { css } from 'styled-components';
import { Spacing4, Spacing8 } from 'autogen/design-tokens/tokens';
import { Typography } from 'components/generic/typography';
import Icon from 'components/Icon';
import Flex from 'components/Layout/components/Flex';
import { InvalidInputStyle, ValidInputDisabledStyle, ValidInputStyle } from 'components/shared/inputs/theme';
import HelperText from '../components/HelperText';
import type { BaseInputProps } from '../shared/types';

const Container = styled.div`
    display: inline-flex;

    // Fix issue with content-visibility: auto; hiding focus outline due it hiding overflow
    &:not(:has(input:focus-visible)) {
        content-visibility: auto;
    }
`;

const LabelText = styled(Typography.Label)`
    line-height: unset;
`;

const LabelContainer = styled.label`
    display: inline-flex;
    align-items: start;
    gap: ${Spacing8};
`;

const Input = styled.input`
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
`;

const Graphics = styled.div<{ $isInvalid?: boolean; $isInderterminate?: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    padding: 3px;
    border-radius: ${({ theme }) => theme.RadiusSmall};
    ${ValidInputStyle}

    ${Input}:focus-visible ~ & {
        outline: ${({ theme }) => theme.OutlineWidth} solid ${({ theme }) => theme.ColorBorderFocusRing};
        outline-offset: ${({ theme }) => theme.OutlineOffset};
    }

    ${Input}:disabled + & {
        ${ValidInputDisabledStyle}
    }

    ${({ $isInvalid }) =>
        $isInvalid &&
        css`
            ${InvalidInputStyle}

            ${Input}:required + & {
                ${InvalidInputStyle}
            }
        `}

    ${Input}:checked + & {
        background-color: ${({ theme, $isInvalid }) =>
            !$isInvalid ? theme.ColorBackgroundBrand : theme.ColorBackgroundError};
        border-color: ${({ theme, $isInvalid }) => (!$isInvalid ? theme.ColorBorderPrimary : theme.ColorBorderError)};
    }

    ${Input}:checked:disabled + & {
        ${ValidInputDisabledStyle}
    }

    ${({ $isInderterminate, theme, $isInvalid }) =>
        $isInderterminate &&
        css`
            background-color: ${!$isInvalid ? theme.ColorBackgroundBrand : theme.ColorBackgroundError};
            border-color: ${!$isInvalid ? theme.ColorBorderPrimary : theme.ColorBorderError};
        `}
`;

const StyledIcon = styled(Icon)<{ $isInderterminate?: boolean }>`
    opacity: 0;

    ${({ $isInderterminate, theme }) =>
        $isInderterminate
            ? css`
                  opacity: 1;
                  fill: ${theme.ColorIconPrimaryInverted};
              `
            : css`
                  ${Input}:checked ~ ${Graphics} & {
                      opacity: 1;

                      fill: ${({ theme }) => theme.ColorIconPrimaryInverted};
                  }

                  ${Input}:not(:checked):disabled ~ ${Graphics} & {
                      fill: ${({ theme }) => theme.ColorIconDisabled};
                      opacity: 0;
                  }

                  ${Input}:checked:disabled ~ ${Graphics} & {
                      fill: ${({ theme }) => theme.ColorIconDisabled};
                  }
              `}
`;

type ControlledCheckboxProps = React.InputHTMLAttributes<HTMLInputElement> & {
    indeterminate?: boolean;
    value?: string | number;
} & BaseInputProps;

const ControlledCheckbox = React.forwardRef<HTMLInputElement, ControlledCheckboxProps>((props, ref) => {
    const { isInvalid: controlledInvalidState, indeterminate, label, helperText, className } = props;
    const [isInvalidUncontrolledState, setIsInvalidUncontrolledState] = useState<boolean>(false);
    const isInvalid = controlledInvalidState || isInvalidUncontrolledState;
    const isIndeterminate = props.checked ? false : indeterminate;
    const helperTextId = useId();

    const checkbox = (
        <Container className={className}>
            <Input
                ref={ref}
                type="checkbox"
                onInvalid={() => setIsInvalidUncontrolledState(true)}
                aria-describedby={helperText ? helperTextId : undefined}
                {...props}
            />
            <Graphics $isInvalid={isInvalid} $isInderterminate={isIndeterminate}>
                <StyledIcon
                    type={isIndeterminate ? 'Minus' : 'Checkmark'}
                    size={16}
                    $isInderterminate={isIndeterminate}
                />
            </Graphics>
        </Container>
    );

    if (label) {
        return (
            <LabelContainer className={className}>
                {checkbox}
                <Flex.Column gap={Spacing4}>
                    <LabelText size="M">{label}</LabelText>
                    {helperText && <HelperText helperText={helperText} helperTextId={helperTextId} />}
                </Flex.Column>
            </LabelContainer>
        );
    }

    return checkbox;
});

export default ControlledCheckbox;
